import { App } from '@capacitor/app'
import { PushNotifications, type Token } from '@capacitor/push-notifications'

import { registerDeviceToken, unregisterDeviceToken } from '@/api/user/deviceTokenService'

import { getNativePlatform, isNativePlatform } from './platform'

let registeredToken: string | null = null

export async function registerNativePush(): Promise<void> {
  if (!isNativePlatform()) return

  try {
    let permission = await PushNotifications.checkPermissions()
    if (permission.receive === 'prompt') {
      permission = await PushNotifications.requestPermissions()
    }
    if (permission.receive !== 'granted') return

    await PushNotifications.register()
  } catch (error) {
    console.warn('Native push registration skipped', error)
  }
}

export function listenForPushRegistration(): () => void {
  if (!isNativePlatform()) return () => undefined

  const registration = PushNotifications.addListener('registration', (token: Token) => {
    registeredToken = token.value
    const platform = getNativePlatform()
    if (platform === 'web') return
    void registerDeviceToken({ token: token.value, platform })
  })

  const registrationError = PushNotifications.addListener('registrationError', () => {
    registeredToken = null
  })

  return () => {
    void registration.then(listener => listener.remove())
    void registrationError.then(listener => listener.remove())
  }
}

export function listenForPushTaps(onPath: (path: string) => void): () => void {
  if (!isNativePlatform()) return () => undefined

  const tap = PushNotifications.addListener('pushNotificationActionPerformed', event => {
    const data = event.notification.data as { path?: string }
    if (data?.path) onPath(data.path)
  })

  return () => {
    void tap.then(listener => listener.remove())
  }
}

export async function unregisterNativePush(): Promise<void> {
  if (!isNativePlatform() || !registeredToken) return
  try {
    await unregisterDeviceToken(registeredToken)
  } finally {
    registeredToken = null
  }
}

export function listenForAppResume(onResume: () => void): () => void {
  if (!isNativePlatform()) return () => undefined
  const handle = App.addListener('resume', onResume)
  return () => {
    void handle.then(listener => listener.remove())
  }
}
